
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { AllVacationsHeader } from "./AllVacationsHeader";
import { VacationFilters } from "./VacationFilters";
import { AllVacationsContent } from "./AllVacationsContent";

interface AllVacationsProps {
  onBack: () => void;
  user?: User | null;
  favorites?: number[];
  onFavoriteChange?: () => void;
}

export const AllVacations = ({ onBack, user = null, favorites = [], onFavoriteChange = () => {} }: AllVacationsProps) => {
  const [vacations, setVacations] = useState<any[]>([]);
  const [displayCount, setDisplayCount] = useState(20);
  const [loading, setLoading] = useState(true);
  const [countryFilter, setCountryFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [useSlider, setUseSlider] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);

  const hasMoreResults = displayCount < vacations.length;
  const displayedVacations = vacations.slice(0, displayCount);

  const { shouldLoadMore, resetLoadMore } = useInfiniteScroll({
    hasNextPage: hasMoreResults,
    isLoading: loading
  });

  useEffect(() => {
    if (shouldLoadMore) {
      setDisplayCount(prev => Math.min(prev + 20, vacations.length));
      resetLoadMore();
    }
  }, [shouldLoadMore, vacations.length, resetLoadMore]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('TUI')
        .select('country')
        .not('country', 'is', null);
      
      if (error) throw error;
      
      const uniqueCountries = [...new Set(data.map(item => item.country))].sort();
      setCountries(uniqueCountries);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchVacations = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null);

      if (countryFilter !== "all") {
        query = query.eq('country', countryFilter);
      }

      if (useSlider) {
        query = query.gte('price', priceRange[0]).lte('price', priceRange[1]);
      } else if (priceFilter === 'budget') {
        query = query.lt('price', 300);
      } else if (priceFilter === 'low') {
        query = query.gte('price', 300).lt('price', 600);
      } else if (priceFilter === 'medium') {
        query = query.gte('price', 600).lt('price', 1200);
      } else if (priceFilter === 'high') {
        query = query.gte('price', 1200).lt('price', 2500);
      } else if (priceFilter === 'luxury') {
        query = query.gte('price', 2500);
      }

      const { data, error } = await query.limit(50);
      
      if (error) throw error;
      
      setVacations(data || []);
    } catch (error: any) {
      toast.error("Fout bij het laden van vakanties");
      console.error('Error fetching vacations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchVacations();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [countryFilter, priceFilter, priceRange, useSlider]);

  return (
    <div className="space-y-6">
      <AllVacationsHeader onBack={onBack} />

      <VacationFilters 
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        useSlider={useSlider}
        setUseSlider={setUseSlider}
        countries={countries}
      />
      
      <AllVacationsContent 
        loading={loading}
        aiSearchLoading={false}
        displayedVacations={displayedVacations}
        vacations={vacations}
        hasMoreResults={hasMoreResults}
        user={user}
        favorites={favorites}
        onFavoriteChange={onFavoriteChange}
      />
    </div>
  );
};
